import Modal from "@/components/common/modal";
import { useUIStore } from "../store/uiStore";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";
import { useWorkflowStore } from "../store/workflowStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type NodeData } from "../types/workflow";
import { getNodeContent } from "../components/node/content";

const NodeErrorsSummaryModal = () => {
  const { nodes } = useWorkflowStore();
  const { nodesWithErrors, setModalState, setSelectedErrors } = useUIStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement[]>([]);

  const nodesAffected = useMemo(() => {
    const affectedNodes: Array<{
      type: string;
      label: string;
      id: string;
    }> = [];

    nodesWithErrors?.forEach((_, nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        const data = node.data as unknown as NodeData;
        affectedNodes.push({
          id: nodeId,
          type: data.type,
          label: data.displayName,
        });
      }
    });

    if (nodesWithErrors?.has("general")) {
      const node = nodesWithErrors.get("general");
      node?.forEach((data) => {
        const key = data[0];
        if (
          key === "flows.check.errors.must_have_at_least_one_finish" ||
          key === "flows.check.errors.must_have_at_all_routes_with_finish_node"
        ) {
          affectedNodes.push({
            id: "general",
            type: "general",
            label: "Falta nodo de finalización",
          });
        }
      });
    }

    return affectedNodes;
  }, [nodesWithErrors, nodes]);

  const [visibleNodes, setVisibleNodes] = useState<typeof nodesAffected>([]);
  const [hiddenNodes, setHiddenNodes] = useState(0);

  const calculateLines = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const items = itemsRef.current;

    let usedLines = 0;
    let currentLineWidth = 0;
    let visibleIndex = 0;
    const GAP = 8; // spacing between elements
    for (let i = 0; i < items.length; i++) {
      if (!items[i]) continue;
      const itemWidth = items[i].offsetWidth + GAP;
      // If the item doesn't fit in the current line
      if (
        currentLineWidth + itemWidth > containerWidth &&
        currentLineWidth > 0
      ) {
        usedLines++;
        currentLineWidth = 0;
      }
      // If we've already used 3 lines, stop
      if (usedLines >= 3) {
        break;
      }
      currentLineWidth += itemWidth;
      visibleIndex = i + 1;
    }
    setVisibleNodes(nodesAffected.slice(0, visibleIndex));
    setHiddenNodes(nodesAffected.length - visibleIndex);
  }, [nodesAffected]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateLines();
    }, 0);
    return () => clearTimeout(timer);
  }, [nodesWithErrors, calculateLines]);

  if (!nodesWithErrors || nodesWithErrors.size === 0) return null;

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        setModalState("idle");
        setSelectedErrors(undefined);
      }}
      title="Resumen de prueba"
      description="Aquí tienes un vistazo de los nodos con fallas en sus configuraciones dentro de este flujo."
      modalContentClassName="w-full max-w-[var(--size-horizontal-sm)]"
    >
      <div className="space-y-4">
        <div ref={containerRef} className="w-full relative py-8">
          <div
            className="flex flex-wrap gap-2 pointer-events-none"
            style={{
              visibility: "hidden",
              position: "absolute",
            }}
          >
            {nodesAffected.map(({ type, label, id }, index) => {
              const content = getNodeContent(type);
              return (
                <div
                  className="flex min-w-0 max-w-full gap-2 items-center rounded-lg bg-surface-danger-accent-quiet px-2 py-1"
                  key={`calculate-error-node-${id}`}
                  ref={(el) => {
                    if (el) {
                      itemsRef.current[index] = el;
                    }
                  }}
                >
                  <Avatar shape="circle" size="2xs">
                    <AvatarFallback shape="circle" type="icon" color="danger">
                      <Icon type={content.icon.type} name={content.icon.name} />
                    </AvatarFallback>
                  </Avatar>
                  <p
                    className="text-danger leading-3xl font-normal"
                    style={{ wordBreak: "break-all", whiteSpace: "normal" }}
                  >
                    {label}
                  </p>
                </div>
              );
            })}
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="visible-nodes-container"
          >
            {visibleNodes.map(({ type, label, id }) => {
              const content = getNodeContent(type);
              return (
                <div
                  className="flex min-w-0 max-w-full gap-2 items-center rounded-lg bg-surface-danger-accent-quiet px-2 py-1"
                  key={`error-node-${id}`}
                >
                  <Avatar shape="circle" size="2xs">
                    <AvatarFallback shape="circle" type="icon" color="danger">
                      <Icon type={content.icon.type} name={content.icon.name} />
                    </AvatarFallback>
                  </Avatar>
                  <p
                    className="text-danger leading-3xl font-normal"
                    style={{ wordBreak: "break-all", whiteSpace: "normal" }}
                  >
                    {label}
                  </p>
                </div>
              );
            })}
            {hiddenNodes > 0 && (
              <div className="flex gap-2 items-center rounded-lg bg-surface-danger-accent-quiet px-2 py-1">
                <p className="text-danger leading-3xl font-normal">
                  y {hiddenNodes} más
                </p>
              </div>
            )}
          </div>
        </div>
        <Button
          variant="tertiary"
          className="w-full"
          onClick={() => {
            setModalState("idle");
            setSelectedErrors(undefined);
          }}
        >
          Aceptar
        </Button>
      </div>
    </Modal>
  );
};

export default NodeErrorsSummaryModal;
